import type { Link } from '@unhead/vue'
import type { Image } from '../../types/media'
import type * as Theme from '#theme'

/**
 * The subset of `<link>` attributes emitted per favicon slot. Typed against
 * `@unhead`'s `Link` directly so `ThemeProvider` can forward the result into
 * `useHead({ link })` without casting.
 */
export type FaviconLink = Link

/**
 * Merges a brand-default favicon bundle with a consumer-site override,
 * slot-by-slot. Site wins per slot — matches the `useThemeLogo` resolution
 * order and the general Nuxt layer-override philosophy (upper layers override
 * lower ones without shadowing the theme file).
 *
 * Both inputs are optional; missing slots resolve to `undefined`, which
 * downstream renderers (`faviconToLinks`) skip.
 */
export function mergeFavicon(
    theme: Theme.Favicon | undefined,
    site: Theme.Favicon | undefined,
): Theme.Favicon {
    return {
        svg: site?.svg ?? theme?.svg,
        ico: site?.ico ?? theme?.ico,
        png: site?.png ?? theme?.png,
        appleTouch: site?.appleTouch ?? theme?.appleTouch,
    }
}

function sizesOf(image: Image | undefined): string | undefined {
    if (!image?.width || !image?.height) return undefined
    return `${image.width}x${image.height}`
}

/**
 * Converts a merged `Favicon` bundle into `<link>` descriptors. Each slot
 * gets a stable `key` so Nuxt's head manager dedupes across re-renders and
 * theme swaps — without it, a runtime theme change would accumulate stale
 * `<link rel="icon">` nodes in the DOM.
 */
export function faviconToLinks(favicon: Theme.Favicon | undefined): FaviconLink[] {
    if (!favicon) return []

    const links: FaviconLink[] = []

    if (favicon.svg?.src) {
        links.push({
            rel: 'icon',
            type: 'image/svg+xml',
            href: favicon.svg.src,
            key: 'theme-favicon-svg',
        })
    }

    if (favicon.ico?.src) {
        links.push({
            rel: 'shortcut icon',
            href: favicon.ico.src,
            key: 'theme-favicon-ico',
        })
    }

    if (favicon.png?.src) {
        const link: FaviconLink = {
            rel: 'icon',
            type: 'image/png',
            href: favicon.png.src,
            key: 'theme-favicon-png',
        }
        const sizes = sizesOf(favicon.png)
        if (sizes) link.sizes = sizes
        links.push(link)
    }

    if (favicon.appleTouch?.src) {
        links.push({
            rel: 'apple-touch-icon',
            sizes: sizesOf(favicon.appleTouch) ?? '180x180',
            href: favicon.appleTouch.src,
            key: 'theme-favicon-apple',
        })
    }

    return links
}
